import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, Plus } from "lucide-react";

const inputCls =
  "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10 transition-all";
const labelCls = "block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5";

export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      {label && <label className={labelCls}>{label}</label>}
      {children}
    </div>
  );
}

export function TextField({ label, value, onChange, testId, type = "text", placeholder = "", required, className }) {
  return (
    <Field label={label} className={className}>
      <input
        type={type} value={value ?? ""} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testId} className={inputCls}
      />
    </Field>
  );
}

export function MoneyField({ label, value, onChange, testId, required, className }) {
  return (
    <Field label={label} className={className}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
        <input
          type="number" step="0.01" min="0" value={value ?? ""} required={required}
          onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
          data-testid={testId} className={`${inputCls} pl-9 font-mono`} placeholder="0,00"
        />
      </div>
    </Field>
  );
}

export function TextArea({ label, value, onChange, testId, className, rows = 2 }) {
  return (
    <Field label={label} className={className}>
      <textarea value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={`${inputCls} resize-none`} />
    </Field>
  );
}

export function SelectField({ label, value, onChange, options, testId, placeholder = "Selecione...", className, required }) {
  const NONE = "__none__";
  const norm = (v) => (v === "" || v === null || v === undefined ? NONE : String(v));
  return (
    <Field label={label} className={className}>
      <Select value={norm(value)} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
        <SelectTrigger className={inputCls} data-testid={testId}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={String(o.value)} value={norm(o.value)} data-testid={testId ? `${testId}-opt-${o.value}` : undefined}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function FormModal({ open, onOpenChange, title, children, onSubmit, submitting, testId, width = "max-w-2xl", submitLabel = "Salvar" }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${width} max-h-[90vh] overflow-y-auto`} data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-slate-900">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          {children}
          <DialogFooter className="gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} data-testid="form-submit-button" className="bg-brand hover:bg-brand-hover text-white font-medium px-5 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Toolbar({ search, setSearch, onNew, newLabel, newTestId, searchTestId, children }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
      <div className="relative max-w-sm w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar..."
          data-testid={searchTestId} className={`${inputCls} pl-9`}
        />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {children}
        {onNew && (
          <button onClick={onNew} data-testid={newTestId} className="bg-brand hover:bg-brand-hover text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-sm shrink-0">
            <Plus className="h-4 w-4" /> {newLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export function TableShell({ headers, children, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {headers.map((h, i) => (
                <th key={i} className={`px-4 py-3.5 ${h.align === "right" ? "text-right" : "text-left"} ${h.sticky ? "sticky right-0 z-10 bg-slate-100 border-l border-slate-200" : ""}`}>{h.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
